import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

const STATUSES = ['Pending', 'Approved', 'Rejected'];

export default function ProposalModal({ proposal, jobId, onSaved, onClose }) {
  const [vendorName, setVendorName] = useState('');
  const [totalCost, setTotalCost] = useState('');
  const [estimatedDays, setEstimatedDays] = useState('');
  const [includes, setIncludes] = useState('');
  const [excludes, setExcludes] = useState('');
  const [warranty, setWarranty] = useState('');
  const [status, setStatus] = useState('Pending');
  const [submittedDate, setSubmittedDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (proposal) {
      setVendorName(proposal.vendor_name || '');
      setTotalCost(proposal.total_cost || '');
      setEstimatedDays(proposal.estimated_days || '');
      setIncludes(proposal.includes || '');
      setExcludes(proposal.excludes || '');
      setWarranty(proposal.warranty || '');
      setStatus(proposal.status || 'Pending');
      setSubmittedDate(proposal.submitted_date || new Date().toISOString().split('T')[0]);
      setNotes(proposal.notes || '');
    }
  }, [proposal]);

  const handleSave = async () => {
    if (!vendorName.trim()) { setError('Vendor name is required.'); return; }
    if (!totalCost) { setError('Total cost is required.'); return; }
    setLoading(true);
    setError('');

    const payload = {
      job_id: jobId,
      vendor_name: vendorName.trim(),
      total_cost: parseFloat(totalCost),
      estimated_days: estimatedDays ? parseInt(estimatedDays) : null,
      includes: includes.trim() || null,
      excludes: excludes.trim() || null,
      warranty: warranty.trim() || null,
      status,
      submitted_date: submittedDate || null,
      notes: notes.trim() || null,
    };

    if (proposal) {
      const { data, error } = await supabase
        .from('proposals')
        .update(payload)
        .eq('id', proposal.id)
        .select()
        .single();
      if (error) { setError(error.message); setLoading(false); return; }
      onSaved(data, false);
    } else {
      const { data, error } = await supabase
        .from('proposals')
        .insert(payload)
        .select()
        .single();
      if (error) { setError(error.message); setLoading(false); return; }
      onSaved(data, true);
    }

    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{proposal ? 'Edit Proposal' : 'Add Proposal'}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {error && <div className="error-msg">{error}</div>}

          <div className="form-group">
            <label>Vendor Name *</label>
            <input
              autoFocus
              value={vendorName}
              onChange={e => setVendorName(e.target.value)}
              placeholder="e.g. Mike's Plumbing"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Total Cost *</label>
              <input
                type="number"
                value={totalCost}
                onChange={e => setTotalCost(e.target.value)}
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label>Estimated Days</label>
              <input
                type="number"
                value={estimatedDays}
                onChange={e => setEstimatedDays(e.target.value)}
                placeholder="e.g. 3"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Includes</label>
            <textarea
              value={includes}
              onChange={e => setIncludes(e.target.value)}
              placeholder="What's covered in this proposal..."
            />
          </div>

          <div className="form-group">
            <label>Excludes</label>
            <textarea
              value={excludes}
              onChange={e => setExcludes(e.target.value)}
              placeholder="What's NOT covered..."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Warranty</label>
              <input
                value={warranty}
                onChange={e => setWarranty(e.target.value)}
                placeholder="e.g. 1 year parts & labor"
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)}>
                {STATUSES.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Submitted Date</label>
              <input
                type="date"
                value={submittedDate}
                onChange={e => setSubmittedDate(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Notes</label>
              <input
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Any notes..."
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : proposal ? 'Save Changes' : 'Add Proposal'}
          </button>
        </div>
      </div>
    </div>
  );
}
